import {
  ReactElement,
  useState,
  useMemo,
  useEffect,
  useCallback,
} from 'react';
import toast from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import Button from '../../components/Button';
import { numberToLetters } from '../../utils/letterNumber';
import {
  DISC_LETTER_NUMBER,
} from '../../constants/apiConstants';
import {
  useBatchUpdateConfigs,
} from '../../hooks/useConfigs';

const EQUIPMENT_FIELDS = [
  'tyre_front_id',
  'tyre_rear_id',
  'wheel_front_id',
  'wheel_rear_id',
  'tyre_front_pressure',
  'tyre_rear_pressure',
  'frame_id',
  'speed_sensor_pos',
  'damper_settings',
] as const;

interface EquipmentCoordinationProps {
  sessions: any[];
  equipmentCatalog: any;
  selectedSessionId: string;
}

function getValidConfigs(session: any): any[] {
  if (!session?.configs) return [];
  return session.configs
    .filter(
      (c: any) =>
        !c.isDeleted &&
        c.letter_number !== undefined &&
        c.letter_number < DISC_LETTER_NUMBER,
    )
    .sort(
      (a: any, b: any) =>
        (a.letter_number ?? 0) - (b.letter_number ?? 0),
    );
}

function lookupName(
  catalog: any,
  table: string,
  id: string | null,
): string {
  if (!id || !catalog?.[table]) return '-';
  const item = catalog[table].find(
    (t: any) => t.id === id,
  );
  return item?.name ?? '-';
}

function formatPressure(v: number | null): string {
  if (v == null) return '-';
  return parseFloat(String(v)).toFixed(2);
}

function sessionLabel(session: any): string {
  const date =
    session.date ||
    (session.created_at
      ? new Date(session.created_at).toLocaleDateString()
      : '');
  const desc =
    session.description || session.name || 'Unnamed';
  return `${date} – ${desc}`;
}

function EquipmentRow({
  label,
  config,
  equipmentCatalog,
  dimmed,
}: {
  label: string;
  config: any;
  equipmentCatalog: any;
  dimmed?: boolean;
}): ReactElement {
  const cls =
    'cr-px-3 cr-py-1.5'
    + (dimmed ? ' cr-text-gray-400' : '');
  return (
    <>
      <td className={cls + ' cr-font-semibold'}>
        {label}
      </td>
      <td className={cls}>
        {config?.description || '-'}
      </td>
      <td className={cls}>
        {lookupName(
          equipmentCatalog,
          'tyres',
          config?.tyre_rear_id,
        )}
      </td>
      <td className={cls}>
        {lookupName(
          equipmentCatalog,
          'wheels_rear',
          config?.wheel_rear_id,
        )}
      </td>
      <td className={cls}>
        {formatPressure(config?.tyre_rear_pressure)}
      </td>
      <td className={cls}>
        {lookupName(
          equipmentCatalog,
          'tyres',
          config?.tyre_front_id,
        )}
      </td>
      <td className={cls}>
        {lookupName(
          equipmentCatalog,
          'wheels_front',
          config?.wheel_front_id,
        )}
      </td>
      <td className={cls}>
        {formatPressure(config?.tyre_front_pressure)}
      </td>
      <td className={cls}>
        {lookupName(
          equipmentCatalog,
          'frames',
          config?.frame_id,
        )}
      </td>
    </>
  );
}

function EquipmentConfigPair({
  config,
  refConfig,
  refConfigs,
  isKeep,
  refId,
  stripe,
  equipmentCatalog,
  onMappingChange,
  onToggle,
}: {
  config: any;
  refConfig: any;
  refConfigs: any[];
  isKeep: boolean;
  refId: string;
  stripe: string;
  equipmentCatalog: any;
  onMappingChange: (refId: string) => void;
  onToggle: (v: 'write' | 'keep') => void;
}): ReactElement {
  const letter = numberToLetters(config.letter_number);
  return (
    <>
      {/* Row 1: Target config equipment */}
      <tr
        className={
          'cr-border-b cr-border-gray-100 ' + stripe
        }
      >
        <EquipmentRow
          label={letter}
          config={config}
          equipmentCatalog={equipmentCatalog}
        />
        {/* Write / Keep radios */}
        <td
          className="cr-px-3 cr-py-1.5"
          rowSpan={2}
        >
          <div
            className={
              'cr-flex cr-flex-col cr-gap-1 '
              + 'cr-items-start'
            }
          >
            <label
              className={
                'cr-flex cr-items-center '
                + 'cr-gap-1 cr-cursor-pointer'
              }
            >
              <input
                type="radio"
                name={`wk-${config.id}`}
                checked={!isKeep}
                onChange={() => onToggle('write')}
                className="cr-text-blue-600"
              />
              <span className="cr-text-xs">
                Write
              </span>
            </label>
            <label
              className={
                'cr-flex cr-items-center '
                + 'cr-gap-1 cr-cursor-pointer'
              }
            >
              <input
                type="radio"
                name={`wk-${config.id}`}
                checked={isKeep}
                onChange={() => onToggle('keep')}
                className="cr-text-blue-600"
              />
              <span className="cr-text-xs">
                Keep
              </span>
            </label>
          </div>
        </td>
      </tr>
      {/* Row 2: Reference config equipment */}
      <tr
        className={
          'cr-border-b-2 cr-border-gray-300 '
          + stripe
        }
      >
        <td className="cr-px-3 cr-py-1.5">
          <select
            value={refId ?? ''}
            disabled={isKeep}
            onChange={(e) =>
              onMappingChange(e.target.value)
            }
            className={
              'cr-w-16 cr-p-1 cr-text-sm '
              + 'cr-border cr-rounded '
              + (isKeep
                ? 'cr-opacity-40 cr-bg-gray-100'
                : 'cr-border-blue-400 '
                  + 'cr-bg-blue-50')
            }
          >
            {refConfigs.map((rc2: any) => (
              <option key={rc2.id} value={rc2.id}>
                {numberToLetters(rc2.letter_number)}
              </option>
            ))}
          </select>
        </td>
        <td
          className={
            'cr-px-3 cr-py-1.5 cr-text-xs '
            + 'cr-italic'
            + (isKeep
              ? ' cr-text-gray-300'
              : ' cr-text-blue-500')
          }
        >
          {refConfig?.description || '-'}
        </td>
        {/* Reference equipment cells */}
        <td
          className={
            'cr-px-3 cr-py-1.5'
            + (isKeep
              ? ' cr-text-gray-300'
              : ' cr-text-blue-500')
          }
        >
          {lookupName(
            equipmentCatalog,
            'tyres',
            refConfig?.tyre_rear_id,
          )}
        </td>
        <td
          className={
            'cr-px-3 cr-py-1.5'
            + (isKeep
              ? ' cr-text-gray-300'
              : ' cr-text-blue-500')
          }
        >
          {lookupName(
            equipmentCatalog,
            'wheels_rear',
            refConfig?.wheel_rear_id,
          )}
        </td>
        <td
          className={
            'cr-px-3 cr-py-1.5'
            + (isKeep
              ? ' cr-text-gray-300'
              : ' cr-text-blue-500')
          }
        >
          {formatPressure(
            refConfig?.tyre_rear_pressure,
          )}
        </td>
        <td
          className={
            'cr-px-3 cr-py-1.5'
            + (isKeep
              ? ' cr-text-gray-300'
              : ' cr-text-blue-500')
          }
        >
          {lookupName(
            equipmentCatalog,
            'tyres',
            refConfig?.tyre_front_id,
          )}
        </td>
        <td
          className={
            'cr-px-3 cr-py-1.5'
            + (isKeep
              ? ' cr-text-gray-300'
              : ' cr-text-blue-500')
          }
        >
          {lookupName(
            equipmentCatalog,
            'wheels_front',
            refConfig?.wheel_front_id,
          )}
        </td>
        <td
          className={
            'cr-px-3 cr-py-1.5'
            + (isKeep
              ? ' cr-text-gray-300'
              : ' cr-text-blue-500')
          }
        >
          {formatPressure(
            refConfig?.tyre_front_pressure,
          )}
        </td>
        <td
          className={
            'cr-px-3 cr-py-1.5'
            + (isKeep
              ? ' cr-text-gray-300'
              : ' cr-text-blue-500')
          }
        >
          {lookupName(
            equipmentCatalog,
            'frames',
            refConfig?.frame_id,
          )}
        </td>
        {/* Action column handled by rowSpan above */}
      </tr>
    </>
  );
}

export default function EquipmentCoordination({
  sessions,
  equipmentCatalog,
  selectedSessionId,
}: EquipmentCoordinationProps): ReactElement {
  const queryClient = useQueryClient();
  const batchUpdate = useBatchUpdateConfigs();

  const [sessionToModifyId, setSessionToModifyId] =
    useState(sessions[1]?.id ?? sessions[0]?.id ?? '');
  const [equipmentRefId, setEquipmentRefId] = useState(
    sessions[0]?.id ?? '',
  );

  const targetSession = useMemo(
    () => sessions.find((s) => s.id === sessionToModifyId),
    [sessions, sessionToModifyId],
  );
  const refSession = useMemo(
    () => sessions.find((s) => s.id === equipmentRefId),
    [sessions, equipmentRefId],
  );

  const targetConfigs = useMemo(
    () => getValidConfigs(targetSession),
    [targetSession],
  );
  const refConfigs = useMemo(
    () => getValidConfigs(refSession),
    [refSession],
  );

  const [letterMapping, setLetterMapping] = useState<
    Record<string, string>
  >({});
  const [writeKeep, setWriteKeep] = useState<
    Record<string, 'write' | 'keep'>
  >({});

  useEffect(() => {
    const newMap: Record<string, string> = {};
    const newWK: Record<string, 'write' | 'keep'> = {};
    targetConfigs.forEach((tc: any, i: number) => {
      const ref = refConfigs[i] ?? refConfigs[0];
      if (ref) newMap[tc.id] = ref.id;
      newWK[tc.id] = 'write';
    });
    setLetterMapping(newMap);
    setWriteKeep(newWK);
  }, [sessionToModifyId, equipmentRefId]);

  const updateMapping = useCallback(
    (configId: string, refConfigId: string) => {
      setLetterMapping((prev) => ({
        ...prev,
        [configId]: refConfigId,
      }));
    },
    [],
  );

  const toggleWriteKeep = useCallback(
    (configId: string, value: 'write' | 'keep') => {
      setWriteKeep((prev) => ({
        ...prev,
        [configId]: value,
      }));
    },
    [],
  );

  const handleWrite = async () => {
    const payloads: any[] = [];
    for (const tc of targetConfigs) {
      if (writeKeep[tc.id] !== 'write') continue;
      const refId = letterMapping[tc.id];
      const rc = refConfigs.find((c: any) => c.id === refId);
      if (!rc) continue;
      const payload: any = { id: tc.id };
      for (const field of EQUIPMENT_FIELDS) {
        payload[field] = rc[field] ?? null;
      }
      payloads.push(payload);
    }
    if (!payloads.length) {
      toast.error('No configs selected to write');
      return;
    }
    try {
      await batchUpdate.mutateAsync(payloads);
      queryClient.invalidateQueries({
        queryKey: [
          'session-tyre-reference',
          selectedSessionId,
        ],
      });
      toast.success(
        `Equipment written to ${payloads.length} config(s)`,
      );
    } catch (err) {
      console.error('Equipment write error:', err);
      toast.error('Failed to write equipment');
    }
  };

  const selectCls =
    'cr-w-full cr-p-2 cr-text-sm cr-text-[#03365D] '
    + 'cr-border cr-border-gray-300 cr-rounded-md '
    + 'focus:cr-ring-2 focus:cr-ring-blue-500 '
    + 'focus:cr-border-blue-500';

  const hasWrites = targetConfigs.some(
    (c: any) => writeKeep[c.id] === 'write',
  );

  return (
    <div className="cr-space-y-4">
      {/* Session Dropdowns */}
      <div
        className={
          'cr-grid cr-grid-cols-1 cr-gap-4 '
          + 'md:cr-grid-cols-2'
        }
      >
        <div>
          <label
            className={
              'cr-block cr-mb-1 cr-text-sm '
              + 'cr-font-medium cr-text-[#03365D]'
            }
          >
            Session to Modify
          </label>
          <select
            value={sessionToModifyId}
            onChange={(e) =>
              setSessionToModifyId(e.target.value)
            }
            className={selectCls}
          >
            {sessions.map((s: any) => (
              <option key={s.id} value={s.id}>
                {sessionLabel(s)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label
            className={
              'cr-block cr-mb-1 cr-text-sm '
              + 'cr-font-medium cr-text-[#03365D]'
            }
          >
            Equipment Reference Session
          </label>
          <select
            value={equipmentRefId}
            onChange={(e) =>
              setEquipmentRefId(e.target.value)
            }
            className={selectCls}
          >
            {sessions.map((s: any) => (
              <option key={s.id} value={s.id}>
                {sessionLabel(s)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Equipment Table — two rows per config */}
      {targetConfigs.length > 0 && (
        <div className="cr-overflow-x-auto">
          <table
            className={
              'cr-w-full cr-text-sm cr-text-left '
              + 'cr-text-[#03365D]'
            }
          >
            <thead>
              <tr
                className={
                  'cr-bg-cirrusBlue-50 cr-text-xs '
                  + 'cr-uppercase'
                }
              >
                <th className="cr-px-3 cr-py-2">Cfg</th>
                <th className="cr-px-3 cr-py-2">
                  Description
                </th>
                <th className="cr-px-3 cr-py-2">
                  Tyre Rear
                </th>
                <th className="cr-px-3 cr-py-2">
                  Wheel Rear
                </th>
                <th className="cr-px-3 cr-py-2">
                  Rear Press.
                </th>
                <th className="cr-px-3 cr-py-2">
                  Tyre Front
                </th>
                <th className="cr-px-3 cr-py-2">
                  Wheel Front
                </th>
                <th className="cr-px-3 cr-py-2">
                  Front Press.
                </th>
                <th className="cr-px-3 cr-py-2">
                  Frame
                </th>
                <th className="cr-px-3 cr-py-2">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {targetConfigs.map(
                (config: any, idx: number) => {
                  const isKeep =
                    writeKeep[config.id] === 'keep';
                  const refId =
                    letterMapping[config.id];
                  const rc = refConfigs.find(
                    (c: any) => c.id === refId,
                  );
                  const stripe = idx % 2
                    ? 'cr-bg-gray-50'
                    : 'cr-bg-white';
                  return (
                    <EquipmentConfigPair
                      key={config.id}
                      config={config}
                      refConfig={rc}
                      refConfigs={refConfigs}
                      isKeep={isKeep}
                      refId={refId}
                      stripe={stripe}
                      equipmentCatalog={
                        equipmentCatalog
                      }
                      onMappingChange={(rid) =>
                        updateMapping(config.id, rid)
                      }
                      onToggle={(v) =>
                        toggleWriteKeep(config.id, v)
                      }
                    />
                  );
                },
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Write button */}
      <div className="cr-flex cr-justify-end cr-pt-2">
        <Button
          onClick={handleWrite}
          disabled={
            !hasWrites || batchUpdate.isPending
          }
          className={
            'cr-px-4 cr-py-2 cr-text-sm '
            + '!cr-font-bold !cr-normal-case '
            + '!cr-bg-[#03365D] !cr-text-white '
            + '!cr-border-[#03365D]'
          }
        >
          {batchUpdate.isPending
            ? 'Writing...'
            : 'Write Equipment Config'}
        </Button>
      </div>
    </div>
  );
}
